import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, Users, Mail, Bell, Activity, LogOut,
  Search, Send, RefreshCw, ChevronRight, Clock, TrendingUp, MessageSquare, Star, X, ArrowLeft,
  Flag, Check, Plus, Trash2, ExternalLink, MapPin, Zap, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const submitterLabel = (list) => {
  const names = (list || []).filter(Boolean);
  const extra = names.length > 3 ? ` +${names.length - 3} more` : '';
  return `${names.slice(0, 3).join(', ')}${extra}`;
};


const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}` };
};

// ===== Drill-Down Panel =====
const DrillDown = ({ title, onClose, children }) => (
  <div className="admin-tab-content">
    <div className="admin-section-header">
      <button onClick={onClose} className="admin-back-btn" style={{ marginBottom: 0 }}>
        <ArrowLeft size={16} /> Back to Overview
      </button>
      <h2 style={{ marginLeft: 12 }}>{title}</h2>
    </div>
    {children}
  </div>
);

// ===== Scores Detail =====
const ScoresDetail = ({ onClose }) => {
  const [data, setData] = useState({ scores: [], total: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/scores`, { headers: getAuthHeaders() });
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);
  return (
    <DrillDown title={`Scorecards (${data.total})`} onClose={onClose}>
      {loading ? <p className="admin-loading">Loading...</p> : data.scores.length === 0 ? (
        <p className="admin-empty">No scorecards logged yet.</p>
      ) : (
        <div className="admin-activity-list">
          {data.scores.map((s, i) => (
            <div key={i} className="admin-card" style={{ padding: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className="admin-activity-user">{s.username}</span>
                  <span className="admin-activity-type" style={{ marginLeft: 8 }}>{s.course_name || 'Unknown Course'}</span>
                </div>
                <span className="admin-activity-time">{s.created_at?.slice(0, 10)}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13, color: '#94a3b8' }}>
                <span>Score: <strong style={{ color: '#f5f0e8' }}>{s.total_score || '-'}</strong></span>
                {s.tees && <span>Tees: {s.tees}</span>}
                {s.holes_played && <span>{s.holes_played} holes</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </DrillDown>
  );
};

// ===== Matches Detail =====
const MatchesDetail = ({ onClose }) => {
  const [data, setData] = useState({ matches: [], total: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/matches`, { headers: getAuthHeaders() });
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);
  return (
    <DrillDown title={`Matches (${data.total})`} onClose={onClose}>
      {loading ? <p className="admin-loading">Loading...</p> : data.matches.length === 0 ? (
        <p className="admin-empty">No matches yet.</p>
      ) : (
        <div className="admin-activity-list">
          {data.matches.map((m, i) => (
            <div key={i} className="admin-activity-row">
              <div className="admin-activity-dot" style={{ background: '#2F8F6B' }} />
              <div className="admin-activity-info" style={{ flex: 1 }}>
                <span className="admin-activity-user">{(m.usernames || []).join(' & ')}</span>
              </div>
              <span className="admin-activity-time">{m.created_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </DrillDown>
  );
};

// ===== Posts Detail =====
const PostsDetail = ({ onClose }) => {
  const [data, setData] = useState({ posts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/posts`, { headers: getAuthHeaders() });
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);
  return (
    <DrillDown title={`Posts (${data.total})`} onClose={onClose}>
      {loading ? <p className="admin-loading">Loading...</p> : data.posts.length === 0 ? (
        <p className="admin-empty">No posts yet.</p>
      ) : (
        <div className="admin-activity-list">
          {data.posts.map((p, i) => (
            <div key={i} className="admin-card" style={{ padding: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="admin-activity-user">{p.username}</span>
                <span className="admin-activity-time">{p.created_at?.slice(0, 10)}</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                {(p.content || '').slice(0, 120)}{(p.content || '').length > 120 ? '...' : ''}
              </p>
              <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: '#64748b' }}>
                <span>{(p.likes || []).length} likes</span>
                <span>{p.comment_count || 0} comments</span>
                {p.photos?.length > 0 && <span>{p.photos.length} photos</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </DrillDown>
  );
};

// ===== Messages Detail =====
const MessagesDetail = ({ onClose }) => {
  const [data, setData] = useState({ total: 0, recent: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/messages-stats`, { headers: getAuthHeaders() });
        setData(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);
  return (
    <DrillDown title={`Messages (${data.total})`} onClose={onClose}>
      {loading ? <p className="admin-loading">Loading...</p> : data.recent.length === 0 ? (
        <p className="admin-empty">No messages yet.</p>
      ) : (
        <div className="admin-activity-list">
          {data.recent.map((m, i) => (
            <div key={i} className="admin-activity-row">
              <div className="admin-activity-dot" style={{ background: '#4B5C55' }} />
              <div className="admin-activity-info" style={{ flex: 1 }}>
                <span className="admin-activity-user">{m.sender_name}</span>
                <span className="admin-activity-type">{(m.content || '').slice(0, 60)}{(m.content || '').length > 60 ? '...' : ''}</span>
              </div>
              <span className="admin-activity-time">{m.timestamp?.slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
        </div>
      )}
    </DrillDown>
  );
};

// ===== Stats Card (clickable) =====
const StatCard = ({ label, value, icon: Icon, accent, onClick, subtitle }) => (
  <div className="admin-stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}
    onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
    <div className="admin-stat-icon" style={{ background: accent || '#0F3B2E' }}>
      <Icon size={18} color="#fff" />
    </div>
    <div style={{ flex: 1 }}>
      <div className="admin-stat-value">{value ?? '-'}</div>
      <div className="admin-stat-label">{label}</div>
      {subtitle && <div style={{ color: '#c9a94e', fontSize: 11, marginTop: 2 }}>{subtitle}</div>}
    </div>
    {onClick && <ChevronRight size={16} style={{ color: '#555' }} />}
  </div>
);

// ===== Mini Chart =====
const MiniBarChart = ({ data }) => {
  if (!data?.length) return <div className="admin-chart-empty">No signup data yet</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="admin-bar-chart">
      {data.slice(-14).map((d, i) => (
        <div key={i} className="admin-bar-col">
          <div
            className="admin-bar"
            style={{ height: `${(d.count / max) * 100}%` }}
            title={`${d.date}: ${d.count}`}
          />
          <span className="admin-bar-label">{d.date?.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

// ===== Overview Tab (with drill-down) =====
const OverviewTabWrapper = ({ stats, featureUsage, refreshStats, setTab, recentSignups }) => {
  const [drillDown, setDrillDown] = useState(null);

  if (drillDown === 'scores') return <ScoresDetail onClose={() => setDrillDown(null)} />;
  if (drillDown === 'matches') return <MatchesDetail onClose={() => setDrillDown(null)} />;
  if (drillDown === 'posts') return <PostsDetail onClose={() => setDrillDown(null)} />;
  if (drillDown === 'messages') return <MessagesDetail onClose={() => setDrillDown(null)} />;

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>Dashboard Overview</h2>
        <button onClick={refreshStats} className="admin-icon-btn" data-testid="refresh-stats">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Key Metrics */}
      <div className="admin-stats-grid">
        <StatCard label="Real Users" value={stats.real_users} icon={Users} accent="#0F3B2E"
          onClick={() => setTab('users')} subtitle={`${stats.total_users} total (incl. bots)`} />
        <StatCard label="New Today" value={stats.new_today} icon={TrendingUp} accent="#2F8F6B"
          onClick={() => setTab('users')} subtitle={`${stats.new_this_week} this week`} />
        <StatCard label="Scorecards" value={stats.total_scores} icon={BarChart3} accent="#C8A96A"
          onClick={() => setDrillDown('scores')} />
        <StatCard label="Matches" value={stats.total_matches} icon={TrendingUp} accent="#7A1E2C"
          onClick={() => setDrillDown('matches')} />
      </div>
      <div className="admin-stats-grid" style={{ marginTop: 12 }}>
        <StatCard label="Posts" value={stats.total_posts} icon={Activity} accent="#1F5A46"
          onClick={() => setDrillDown('posts')} />
        <StatCard label="Messages" value={stats.total_messages} icon={MessageSquare} accent="#4B5C55"
          onClick={() => setDrillDown('messages')} />
        <StatCard label="Daily Active" value={stats.daily_active} icon={Clock} accent="#2F8F6B"
          onClick={() => setTab('activity')} />
        <StatCard label="Weekly Active" value={stats.weekly_active} icon={Clock} accent="#8A8F86"
          onClick={() => setTab('activity')} />
      </div>

      {/* Recent Signups */}
      <div className="admin-card" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 className="admin-card-title" style={{ margin: 0 }}>Recent Signups (Real Users)</h3>
          <button onClick={() => setTab('users')} className="admin-icon-btn" style={{ fontSize: 12 }}>
            View All <ChevronRight size={14} />
          </button>
        </div>
        {(!recentSignups || recentSignups.length === 0) ? (
          <p className="admin-empty">No new signups in the last 7 days</p>
        ) : (
          <div className="admin-activity-list">
            {recentSignups.map((u, i) => (
              <div key={i} className="admin-activity-row" style={{ cursor: 'pointer' }} onClick={() => setTab('users')}>
                <div className="admin-activity-dot" style={{ background: u.profile_complete ? '#2F8F6B' : '#C8A96A' }} />
                <div className="admin-activity-info" style={{ flex: 1 }}>
                  <span className="admin-activity-user">{u.display_name || u.username}</span>
                  <span className="admin-activity-type">{u.email}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className="admin-activity-time">{u.created_at?.slice(0,10)}</span>
                  <div style={{ fontSize: 10, color: u.profile_complete ? '#2F8F6B' : '#C8A96A', marginTop: 2 }}>
                    {u.profile_complete ? 'Profile Complete' : 'Incomplete'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signup Chart */}
      <div className="admin-card" style={{ marginTop: 16 }}>
        <h3 className="admin-card-title">Signups (Last 30 Days)</h3>
        <MiniBarChart data={stats.signups_over_time} />
      </div>

      {/* Feature Usage */}
      {featureUsage?.length > 0 && (
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h3 className="admin-card-title">Feature Usage (7 Days)</h3>
          <div className="admin-feature-list">
            {featureUsage.map((f, i) => (
              <div key={i} className="admin-feature-row">
                <span className="admin-feature-name">{f.event_type?.replace(/_/g,' ')}</span>
                <span className="admin-feature-count">{f.count} events / {f.unique_users} users</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Users Tab =====
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [hideBots, setHideBots] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState([]);

  const fetchUsers = useCallback(async (q = '', bots = true) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeaders(),
        params: { search: q, limit: 50, hide_bots: bots }
      });
      setUsers(res.data.users); setTotal(res.data.total);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers('', hideBots); }, [fetchUsers, hideBots]);

  const viewUser = async (u) => {
    setSelectedUser(u);
    try {
      const res = await axios.get(`${API}/admin/user/${u.id}/activity`, { headers: getAuthHeaders() });
      setUserActivity(res.data);
    } catch { setUserActivity([]); }
  };

  const resetSwipes = async () => {
    if (!window.confirm(`Reset ALL swipes, matches and follows for ${selectedUser.username}? They will be able to re-swipe on everyone.`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${selectedUser.id}/reset-swipes`, {}, { headers: getAuthHeaders() });
      alert(`Reset done — removed ${res.data.swipes_deleted} swipes, ${res.data.matches_deleted} matches, ${res.data.follows_deleted} follows.`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Reset failed');
    }
  };

  const toggleFounder = async () => {
    const enabled = !selectedUser.is_founder;
    if (!window.confirm(`${enabled ? 'Grant' : 'Revoke'} Founder status for ${selectedUser.username}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/founder`, { enabled }, { headers: getAuthHeaders() });
      setSelectedUser({ ...selectedUser, is_founder: enabled });
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  };

  if (selectedUser) return (
    <div className="admin-tab-content">
      <button onClick={() => setSelectedUser(null)} className="admin-back-btn" data-testid="back-to-users">
        &larr; Back to Users
      </button>
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h3 className="admin-card-title">{selectedUser.display_name || selectedUser.username}</h3>
          <span style={{
            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
            background: selectedUser.profile_complete ? 'rgba(47,143,107,0.2)' : 'rgba(200,169,106,0.2)',
            color: selectedUser.profile_complete ? '#2F8F6B' : '#C8A96A'
          }}>
            {selectedUser.profile_complete ? 'Profile Complete' : 'Incomplete'}
          </span>
        </div>
        <div className="admin-user-details" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginTop: 12 }}>
          <p><strong>Username:</strong> {selectedUser.username || 'N/A'}</p>
          <p><strong>Email:</strong> {selectedUser.email || 'N/A'}</p>
          <p><strong>Joined:</strong> {selectedUser.created_at?.slice(0,10) || 'N/A'}</p>
          <p><strong>Last Active:</strong> {selectedUser.last_active?.slice(0,16).replace('T',' ') || 'Never'}</p>
          <p><strong>Location:</strong> {selectedUser.location || 'N/A'}</p>
          <p><strong>Handicap:</strong> {selectedUser.handicap ?? 'N/A'}</p>
          <p><strong>Gender:</strong> {selectedUser.gender || 'N/A'}</p>
          <p><strong>Age:</strong> {selectedUser.age || 'N/A'}</p>
          <p><strong>Events:</strong> {selectedUser.event_count || 0}</p>
          <p><strong>Notifications:</strong> {selectedUser.notifications_enabled ? 'Enabled' : 'Disabled'}</p>
          <p><strong>Push Subscription:</strong> {selectedUser.has_push_subscription ? 'Active' : 'None'}</p>
          <p><strong>Platinum:</strong> {selectedUser.is_platinum ? 'Yes' : 'No'}</p>
          <p><strong>Founder:</strong> {selectedUser.is_founder ? 'Yes' : 'No'}</p>
          <p><strong>GHIN:</strong> {selectedUser.ghin || 'N/A'}</p>
        </div>
        <button
          onClick={toggleFounder}
          style={{ marginTop: 16, marginRight: 8, padding: '8px 16px', borderRadius: 8, border: '1px solid #D4AF5A', background: selectedUser.is_founder ? 'rgba(212,175,90,0.25)' : 'rgba(212,175,90,0.08)', color: '#D4AF5A', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          data-testid="toggle-founder-btn"
        >
          {selectedUser.is_founder ? 'Revoke Founder' : 'Make Founder'}
        </button>
        <button
          onClick={resetSwipes}
          style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid #C8A96A', background: 'rgba(200,169,106,0.1)', color: '#C8A96A', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          data-testid="reset-swipes-btn"
        >
          Reset Swipes / Matches / Follows
        </button>
      </div>
      <div className="admin-card" style={{ marginTop: 12 }}>
        <h3 className="admin-card-title">Recent Activity ({userActivity.length})</h3>
        {userActivity.length === 0 ? (
          <p className="admin-empty">No activity recorded</p>
        ) : (
          <div className="admin-activity-list">
            {userActivity.slice(0, 50).map((e, i) => (
              <div key={i} className="admin-activity-row">
                <span className="admin-activity-type">{e.event_type?.replace(/_/g,' ')}</span>
                <span className="admin-activity-time">{e.timestamp?.slice(0,16).replace('T',' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>Users ({total})</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setHideBots(!hideBots)}
            data-testid="toggle-bots"
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: hideBots ? '2px solid #c9a94e' : '2px solid #333',
              background: hideBots ? 'rgba(201,169,78,0.15)' : 'transparent',
              color: hideBots ? '#c9a94e' : '#aaa',
            }}
          >
            {hideBots ? 'Real Users Only' : 'All Users'}
          </button>
        </div>
      </div>
      <div className="admin-search-bar">
        <Search size={16} />
        <input
          data-testid="admin-user-search"
          type="text"
          placeholder="Search by username, display name, or email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); fetchUsers(e.target.value, hideBots); }}
          className="admin-search-input"
        />
      </div>
      {loading ? <p className="admin-loading">Loading...</p> : (
        <div className="admin-users-list">
          {users.map(u => (
            <div key={u.id} className="admin-user-row" onClick={() => viewUser(u)} data-testid={`user-row-${u.username}`}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="admin-user-name">{u.display_name || u.username}</span>
                  {u.is_founder && <span style={{ fontSize: 10, color: '#0F1E1A', background: '#D4AF5A', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>FOUNDER</span>}
                  {u.is_platinum && <span style={{ fontSize: 10, color: '#c9a94e', border: '1px solid #c9a94e', padding: '1px 6px', borderRadius: 8 }}>PLATINUM</span>}
                  {u.has_push_subscription && <span style={{ fontSize: 10, color: '#2F8F6B', border: '1px solid #2F8F6B', padding: '1px 6px', borderRadius: 8 }}>PUSH</span>}
                  {!u.profile_complete && <span style={{ fontSize: 10, color: '#C8A96A', opacity: 0.7 }}>incomplete</span>}
                </div>
                <span className="admin-user-email">{u.email || ''}</span>
              </div>
              <div className="admin-user-meta" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Joined {u.created_at?.slice(0,10)}</span>
                <span style={{ fontSize: 11, color: u.event_count > 0 ? '#2F8F6B' : '#555' }}>
                  {u.event_count || 0} events{u.last_active ? ` · last ${u.last_active.slice(5,10)}` : ''}
                </span>
              </div>
              <ChevronRight size={16} style={{ color: '#555', marginLeft: 8 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Email Tab =====
const APP_STORE_URL = 'https://apps.apple.com/us/app/the-club-golf/id6794729488';
const APP_STORE_REVIEW_URL = 'https://apps.apple.com/us/app/the-club-golf/id6794729488?action=write-review';
const PLAY_URL = 'https://play.google.com/store/apps/details?id=co.theclubgolf.app';
const SMART_LINK = 'https://theclubgolf.co/download';
const PROGRESS_DRAFT_SUBJECT = "The Club is live on the App Store & Google Play ⛳";
const EMAIL_ASSETS = 'https://theclubgolf.co';
const PROGRESS_DRAFT_HTML = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="color-scheme" content="light dark"><meta name="supported-color-schemes" content="light dark"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#F3F0E8;">
<div style="background:#F3F0E8;padding:28px 12px;font-family:Georgia,'Times New Roman',serif;">
  <div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:10px;overflow:hidden;border:1px solid #E7DDC7;">
    <img src="${EMAIL_ASSETS}/email/header-live.png" alt="The Club — We're Live" width="600" style="display:block;width:100%;height:auto;" />
    <div style="padding:30px 32px 36px;">
      <h1 style="color:#1a2332;font-size:27px;line-height:1.25;margin:0 0 20px;text-align:center;">The Club is on both app stores</h1>
      <p style="color:#4b5563;font-size:15px;line-height:1.7;margin:0 0 16px;">You were one of the first to sign up, so you're hearing it first: <strong style="color:#1a2332;">The Club Golf is officially live on the Apple App Store and Google Play.</strong> Find playing partners near you, run Nassau, Skins, Wolf and more with live scoring, and settle up when the round's done.</p>

      <h2 style="color:#1a2332;font-size:17px;margin:30px 0 14px;">1. Download the app</h2>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 14px;"><tr><td width="50%" style="padding:4px;text-align:center;"><a href="${APP_STORE_URL}" style="display:block;"><img src="${EMAIL_ASSETS}/email/btn-appstore.png" alt="Download on the App Store" width="240" style="width:100%;max-width:240px;height:auto;display:inline-block;border:0;" /></a></td><td width="50%" style="padding:4px;text-align:center;"><a href="${PLAY_URL}" style="display:block;"><img src="${EMAIL_ASSETS}/email/btn-googleplay.png" alt="Get it on Google Play" width="240" style="width:100%;max-width:240px;height:auto;display:inline-block;border:0;" /></a></td></tr></table>
      <p style="color:#6b7280;font-size:13px;line-height:1.6;text-align:center;margin:0 0 6px;font-family:Arial,sans-serif;">Or open <a href="${SMART_LINK}" style="color:#1a2332;font-weight:bold;">theclubgolf.co/download</a> on any phone — it picks the right store for you.</p>
      <p style="color:#6b7280;font-size:13px;line-height:1.6;text-align:center;margin:0 0 24px;font-family:Arial,sans-serif;">Already using the web version? Same login works in the app.</p>

      <h2 style="color:#1a2332;font-size:17px;margin:30px 0 14px;">2. Leave a quick rating (30 seconds, huge help)</h2>
      <p style="color:#4b5563;font-size:15px;line-height:1.7;margin:0 0 16px;">New apps live or die by their first ratings. If The Club is useful to you, a 5-star rating is the single best thing you can do to help other golfers find it.</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 14px;"><tr><td width="50%" style="padding:4px;text-align:center;"><a href="${APP_STORE_REVIEW_URL}" style="display:block;"><img src="${EMAIL_ASSETS}/email/btn-rate-appstore.png" alt="Rate on the App Store" width="240" style="width:100%;max-width:240px;height:auto;display:inline-block;border:0;" /></a></td><td width="50%" style="padding:4px;text-align:center;"><a href="${PLAY_URL}" style="display:block;"><img src="${EMAIL_ASSETS}/email/btn-rate-googleplay.png" alt="Rate on Google Play" width="240" style="width:100%;max-width:240px;height:auto;display:inline-block;border:0;" /></a></td></tr></table>

      <h2 style="color:#1a2332;font-size:17px;margin:30px 0 14px;">3. Bring your crew</h2>
      <p style="color:#4b5563;font-size:15px;line-height:1.7;margin:0 0 16px;">The Club works best with the people you actually play with. Forward this email, or text your group this:</p>
      <div style="border:1px solid #E7DDC7;border-radius:6px;padding:14px 18px;margin:0 0 18px;font-family:Arial,sans-serif;font-size:14px;color:#1a2332;line-height:1.6;">
        Hey — I'm on The Club, a golf app for finding games and running side bets with live scoring. Grab it here and add me: <strong>theclubgolf.co/download</strong>
      </div>
      <div style="text-align:center;margin:0 0 8px;">
        <img src="${EMAIL_ASSETS}/qr-download.png" alt="Scan to download The Club" width="220" style="width:220px;height:auto;border-radius:8px;display:inline-block;" />
      </div>
      <p style="color:#6b7280;font-size:12px;text-align:center;margin:0 0 28px;font-family:Arial,sans-serif;">Show this QR at the clubhouse or on the first tee — it works for iPhone and Android.</p>

      <p style="color:#4b5563;font-size:15px;line-height:1.7;margin:0 0 16px;">Once you're in, follow me and say hello — I read every message. Your feedback shapes what we build next.</p>
      <p style="color:#4b5563;font-size:15px;line-height:1.7;margin:0;">See you on the course,<br/><strong style="color:#1a2332;">Marc Viglio</strong><br/><span style="color:#6b7280;font-family:Arial,sans-serif;font-size:12px;letter-spacing:1px;text-transform:uppercase;">Founder, The Club</span></p>
    </div>
    <div style="padding:18px 32px;border-top:1px solid #E7DDC7;text-align:center;">
      <p style="color:#9ca3af;font-size:12px;line-height:1.6;margin:0;font-family:Arial,sans-serif;">The Club Golf is owned and operated by Clubhouse Labs, LLC<br/>Questions? <a href="mailto:support@theclubgolf.co" style="color:#4b5563;">support@theclubgolf.co</a></p>
    </div>
  </div>
</div>
</body></html>`;

const EmailTab = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [audience, setAudience] = useState('all');
  const [emailList, setEmailList] = useState([]);
  const [launchList, setLaunchList] = useState([]);
  const [showList, setShowList] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-history`, { headers: getAuthHeaders() });
      setHistory(res.data);
    } catch {}
  }, []);

  const fetchEmailList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-list?hide_bots=true`, { headers: getAuthHeaders() });
      setEmailList(res.data);
    } catch {}
    try {
      const res = await axios.get(`${API}/admin/launch-list`, { headers: getAuthHeaders() });
      setLaunchList(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchHistory(); fetchEmailList(); }, [fetchHistory, fetchEmailList]);

  const sendEmail = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body required'); return; }
    setSending(true);
    try {
      const payload = { subject, html_content: body };
      if (audience === 'test') {
        payload.recipient_emails = ['marc@theclubgolf.co'];
      } else if (audience === 'all_launch') {
        payload.include_launch_list = true;
      }
      const res = await axios.post(`${API}/admin/send-email`, payload, { headers: getAuthHeaders() });
      if (res.data.status === 'in_progress') {
        toast.success(`Broadcast queued to ${res.data.queued} recipients — sending in the background. Watch Send History for progress.`);
      } else {
        toast.success(`Email sent to ${res.data.sent} ${audience === 'test' ? '(test)' : 'recipients'}`);
      }
      setSubject(''); setBody('');
      fetchHistory();
      setTimeout(fetchHistory, 8000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header"><h2>Email Broadcast</h2></div>

      {/* Email List */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="admin-card-title" style={{ margin: 0 }}>
            Email Recipients ({emailList.length} real users · {launchList.length} launch-list signups)
          </h3>
          <button onClick={() => setShowList(!showList)} className="admin-icon-btn" style={{ fontSize: 12 }}
            data-testid="toggle-email-list">
            {showList ? 'Hide List' : 'Show List'}
          </button>
        </div>
        {showList && (
          <div className="admin-activity-list" style={{ marginTop: 12, maxHeight: 300, overflowY: 'auto' }}>
            {emailList.map((u, i) => (
              <div key={i} className="admin-activity-row" style={{ padding: '6px 0' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span className="admin-activity-user">{u.display_name || u.username}</span>
                </div>
                <span style={{ color: '#94a3b8', fontSize: 13, fontFamily: 'monospace' }}>{u.email}</span>
                <span className="admin-activity-time" style={{ marginLeft: 12 }}>{u.created_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose */}
      <div className="admin-card">
        <div className="admin-field">
          <label className="admin-label">Audience</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ v: 'all', l: 'All Users' }, { v: 'all_launch', l: 'Users + Launch List' }, { v: 'test', l: 'Test (Only Me)' }].map(o => (
              <button key={o.v} onClick={() => setAudience(o.v)}
                data-testid={`email-audience-${o.v}`}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: audience === o.v ? '2px solid #c9a94e' : '2px solid #333',
                  background: audience === o.v ? 'rgba(201,169,78,0.15)' : 'transparent',
                  color: audience === o.v ? '#c9a94e' : '#aaa',
                }}
              >{o.l}</button>
            ))}
          </div>
        </div>
        <div className="admin-field">
          <label className="admin-label">Subject</label>
          <input
            data-testid="email-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="admin-input"
            placeholder="Email subject line"
          />
        </div>
        <div className="admin-field" style={{ marginBottom: 8 }}>
          <button
            data-testid="load-progress-draft-btn"
            onClick={() => { setSubject(PROGRESS_DRAFT_SUBJECT); setBody(PROGRESS_DRAFT_HTML); toast.success('Launch email draft loaded — send a test to yourself first'); }}
            className="admin-icon-btn"
            style={{ fontSize: 13, color: '#c9a94e', border: '1px dashed #c9a94e', padding: '8px 14px', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}
          >
            Load "We're Live" Launch Email Draft
          </button>
        </div>
        <div className="admin-field">
          <label className="admin-label">Body (HTML)</label>
          <textarea
            data-testid="email-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="admin-textarea"
            rows={8}
            placeholder="<h1>Hello!</h1><p>Your email content here...</p>"
          />
        </div>
        <button
          data-testid="send-email-btn"
          onClick={sendEmail}
          disabled={sending}
          className="admin-primary-btn"
        >
          {sending ? 'Sending...' : <><Send size={16} /> {audience === 'test' ? 'Send Test Email' : audience === 'all_launch' ? 'Send to Users + Launch List' : 'Send to All Users'}</>}
        </button>
      </div>
      {history.length > 0 && (
        <div className="admin-card" style={{ marginTop: 16 }}>
          <h3 className="admin-card-title">Send History</h3>
          <div className="admin-history-list">
            {history.map((h, i) => (
              <div key={i} className="admin-history-row">
                <div>
                  <span className="admin-history-subject">{h.subject}</span>
                  <span className="admin-history-meta">
                    {h.sent} sent / {h.failed} failed{h.status === 'in_progress' ? ` of ${h.recipient_count} — sending…` : ''}
                  </span>
                </div>
                <span className="admin-history-time">{h.sent_at?.slice(0,16).replace('T',' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Push Tab =====
const PushTab = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [audience, setAudience] = useState('all');
  const [nudging, setNudging] = useState(false);
  const [pushStats, setPushStats] = useState({ enabled: 0, total: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/users?limit=1000&hide_bots=true`, { headers: getAuthHeaders() });
        const users = res.data.users || [];
        const enabled = users.filter(u => u.has_push_subscription).length;
        setPushStats({ enabled, total: users.length });
      } catch {}
    })();
  }, []);

  const sendPush = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and body required'); return; }
    setSending(true);
    try {
      const payload = { title, body };
      if (audience === 'test') {
        payload.target_user_ids = ['d7a0f899-c78e-4bd5-87e9-adf810add3fd'];
      }
      const res = await axios.post(`${API}/admin/send-push`, payload, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setTitle(''); setBody('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  const sendNudge = async () => {
    setNudging(true);
    try {
      const res = await axios.post(`${API}/admin/send-push-nudge`, {}, { headers: getAuthHeaders() });
      toast.success(`Nudge sent to ${res.data.sent} users (${res.data.targeted} targeted)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send nudge');
    }
    setNudging(false);
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header"><h2>Push Notifications</h2></div>

      {/* Push Stats */}
      <div className="admin-card" style={{ marginBottom: 16, display: 'flex', gap: 24 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#2F8F6B' }}>{pushStats.enabled}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Users with push enabled</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#C8A96A' }}>{pushStats.total - pushStats.enabled}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Without push</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#f5f0e8' }}>{pushStats.total}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>Total real users</div>
        </div>
      </div>

      {/* Nudge Section */}
      <div className="admin-card" style={{ marginBottom: 16, borderLeft: '3px solid #c9a94e' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Notification Nudge</h3>
            <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
              Send an email to all users who haven't enabled push notifications yet, asking them to turn it on.
            </p>
          </div>
          <button onClick={sendNudge} disabled={nudging} className="admin-primary-btn" style={{ minWidth: 140 }}
            data-testid="send-nudge-btn">
            {nudging ? 'Sending...' : <><Mail size={14} /> Send Nudge Email</>}
          </button>
        </div>
      </div>

      {/* Broadcast Section */}
      <div className="admin-card">
        <div className="admin-field">
          <label className="admin-label">Audience</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ v: 'all', l: 'All Users' }, { v: 'test', l: 'Test (Only Me)' }].map(o => (
              <button key={o.v} onClick={() => setAudience(o.v)}
                data-testid={`push-audience-${o.v}`}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: audience === o.v ? '2px solid #c9a94e' : '2px solid #333',
                  background: audience === o.v ? 'rgba(201,169,78,0.15)' : 'transparent',
                  color: audience === o.v ? '#c9a94e' : '#aaa',
                }}
              >{o.l}</button>
            ))}
          </div>
        </div>
        <div className="admin-field">
          <label className="admin-label">Title</label>
          <input
            data-testid="push-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="admin-input"
            placeholder="Notification title"
          />
        </div>
        <div className="admin-field">
          <label className="admin-label">Body</label>
          <textarea
            data-testid="push-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="admin-textarea"
            rows={4}
            placeholder="Notification message..."
          />
        </div>
        <button
          data-testid="send-push-btn"
          onClick={sendPush}
          disabled={sending}
          className="admin-primary-btn"
        >
          {sending ? 'Sending...' : <><Bell size={16} /> {audience === 'test' ? 'Send Test Notification' : 'Broadcast to All Users'}</>}
        </button>
      </div>
    </div>
  );
};

// ===== Activity Tab =====
const ActivityTab = () => {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/recent-activity`, { headers: getAuthHeaders(), params: { limit: 100 } });
      setActivity(res.data);
    } catch { toast.error('Failed to load activity'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>Live Activity Feed</h2>
        <button onClick={fetchActivity} className="admin-icon-btn" data-testid="refresh-activity">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>
      {loading ? <p className="admin-loading">Loading...</p> : activity.length === 0 ? (
        <p className="admin-empty">No activity recorded yet. User events will appear here as people use the app.</p>
      ) : (
        <div className="admin-activity-list">
          {activity.map((e, i) => (
            <div key={i} className="admin-activity-row">
              <div className="admin-activity-dot" />
              <div className="admin-activity-info">
                <span className="admin-activity-user">{e.username}</span>
                <span className="admin-activity-type">{e.event_type?.replace(/_/g,' ')}</span>
              </div>
              <span className="admin-activity-time">{e.timestamp?.slice(11,16)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Feedback Tab =====
const FeedbackTab = () => {
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/feedback`, { headers: getAuthHeaders() });
      setFeedback(res.data.feedback); setTotal(res.data.total);
    } catch { toast.error('Failed to load feedback'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const starDisplay = (r) => '★'.repeat(r) + '☆'.repeat(5 - r);
  const avg = feedback.length > 0 ? (feedback.reduce((a, f) => a + f.rating, 0) / feedback.length).toFixed(1) : '-';

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>User Feedback ({total})</h2>
        <button onClick={fetchFeedback} className="admin-icon-btn" data-testid="refresh-feedback">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>
      <div className="admin-stats-grid" style={{ marginBottom: 16 }}>
        <StatCard label="Total Feedback" value={total} icon={MessageSquare} accent="#0F3B2E" />
        <StatCard label="Avg Rating" value={avg} icon={Star} accent="#C8A96A" />
      </div>
      {loading ? <p className="admin-loading">Loading...</p> : feedback.length === 0 ? (
        <p className="admin-empty">No feedback received yet. Users can submit feedback via Settings or after scoring.</p>
      ) : (
        <div className="admin-activity-list" style={{ gap: 8 }}>
          {feedback.map((f, i) => (
            <div key={i} className="admin-card" style={{ padding: 14 }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="admin-activity-user">{f.username || 'Unknown'}</span>
                  <span className="admin-activity-type" style={{ textTransform: 'capitalize' }}>{f.context}</span>
                </div>
                <span className="admin-activity-time">{f.created_at?.slice(0,16).replace('T',' ')}</span>
              </div>
              <div style={{ color: '#C8A96A', fontSize: 16, letterSpacing: 2, marginBottom: 4 }}>{starDisplay(f.rating)}</div>
              {f.message && <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>{f.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Fulfill Modal: enter 18 pars for a requested course =====
const FulfillModal = ({ request, onClose, onSaved }) => {
  const [pars, setPars] = useState(Array(18).fill(4));
  const [teeName, setTeeName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const updatePar = (i, v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return;
    const next = [...pars];
    next[i] = Math.max(3, Math.min(6, n));
    setPars(next);
  };

  const { total, front9, back9 } = useMemo(() => ({
    total: pars.reduce((a, b) => a + b, 0),
    front9: pars.slice(0, 9).reduce((a, b) => a + b, 0),
    back9: pars.slice(9, 18).reduce((a, b) => a + b, 0),
  }), [pars]);
  const valid = pars.every(p => p >= 3 && p <= 6);

  const save = async () => {
    if (!valid) { toast.error('Each par must be 3, 4, 5, or 6'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/course-requests/${request.id}/fulfill`,
        { pars, tee_name: teeName || null, notes: notes || null },
        { headers: getAuthHeaders() }
      );
      toast.success(`Scorecard saved. ${request.user_display || 'User'} notified by email.`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save scorecard');
    }
    setSaving(false);
  };

  return (
    <div
      className="admin-modal-backdrop"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        className="admin-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}
        data-testid="fulfill-course-modal"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ color: '#f5f0e8', margin: 0, fontSize: 18 }}>Fulfill: {request.course_name}</h3>
            <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
              Requested by {request.user_display || 'unknown'} • {request.address || 'no address'}
            </p>
            {request.course_url && (
              <a href={request.course_url} target="_blank" rel="noopener noreferrer" style={{ color: '#C8A96A', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <ExternalLink size={12} /> Open course site
              </a>
            )}
            {request.notes && (
              <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>"{request.notes}"</p>
            )}
          </div>
          <button onClick={onClose} className="admin-icon-btn" data-testid="close-fulfill-modal"><X size={16} /></button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Tee Name (optional)</label>
          <input
            type="text"
            value={teeName}
            onChange={(e) => setTeeName(e.target.value)}
            placeholder="e.g. White / Blue / Championship"
            className="admin-input"
            data-testid="fulfill-tee-name"
            style={{ width: '100%', padding: 8, background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>Per-Hole Pars</span>
            <span style={{ color: total === 72 ? '#2F8F6B' : '#C8A96A', fontSize: 12, fontWeight: 600 }}>Total: {total}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 6 }}>
            {pars.slice(0, 9).map((p, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>{i + 1}</div>
                <input
                  type="number"
                  min={3}
                  max={6}
                  value={p}
                  onChange={(e) => updatePar(i, e.target.value)}
                  data-testid={`par-input-${i + 1}`}
                  style={{ width: '100%', padding: 6, textAlign: 'center', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 14 }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 6, marginTop: 8 }}>
            {pars.slice(9, 18).map((p, i) => (
              <div key={i + 9} style={{ textAlign: 'center' }}>
                <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>{i + 10}</div>
                <input
                  type="number"
                  min={3}
                  max={6}
                  value={p}
                  onChange={(e) => updatePar(i + 9, e.target.value)}
                  data-testid={`par-input-${i + 10}`}
                  style={{ width: '100%', padding: 6, textAlign: 'center', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 14 }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
            Front 9: {front9} • Back 9: {back9}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Internal Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Source URL, tee info, etc."
            rows={2}
            data-testid="fulfill-notes"
            style={{ width: '100%', padding: 8, background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="admin-icon-btn" data-testid="cancel-fulfill">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !valid}
            className="admin-icon-btn"
            data-testid="save-fulfill"
            style={{ background: '#2F8F6B', color: '#fff', opacity: saving || !valid ? 0.6 : 1 }}
          >
            <Check size={14} /> {saving ? 'Saving...' : 'Save & Notify User'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Add New Scorecard Modal (proactive curation, no request) =====
const AddScorecardModal = ({ onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [teeName, setTeeName] = useState('');
  const [pars, setPars] = useState(Array(18).fill(4));
  const [saving, setSaving] = useState(false);

  const updatePar = (i, v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return;
    const next = [...pars];
    next[i] = Math.max(3, Math.min(6, n));
    setPars(next);
  };

  const total = pars.reduce((a, b) => a + b, 0);
  const valid = name.trim() && pars.every(p => p >= 3 && p <= 6);

  const save = async () => {
    if (!valid) { toast.error('Course name + valid 18 pars required'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/scorecards`,
        { course_name: name.trim(), pars, tee_name: teeName || null },
        { headers: getAuthHeaders() }
      );
      toast.success(`Scorecard saved for ${name}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save scorecard');
    }
    setSaving(false);
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        className="admin-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}
        data-testid="add-scorecard-modal"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: '#f5f0e8', margin: 0, fontSize: 18 }}>Add Scorecard</h3>
          <button onClick={onClose} className="admin-icon-btn"><X size={16} /></button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Course Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mill Creek Park Golf Course"
            data-testid="add-course-name"
            style={{ width: '100%', padding: 8, background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Tee Name (optional)</label>
          <input
            type="text"
            value={teeName}
            onChange={(e) => setTeeName(e.target.value)}
            placeholder="e.g. Blue"
            style={{ width: '100%', padding: 8, background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>Per-Hole Pars</span>
            <span style={{ color: total === 72 ? '#2F8F6B' : '#C8A96A', fontSize: 12, fontWeight: 600 }}>Total: {total}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 6 }}>
            {pars.slice(0, 9).map((p, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>{i + 1}</div>
                <input
                  type="number" min={3} max={6} value={p}
                  onChange={(e) => updatePar(i, e.target.value)}
                  style={{ width: '100%', padding: 6, textAlign: 'center', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 14 }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 6, marginTop: 8 }}>
            {pars.slice(9, 18).map((p, i) => (
              <div key={i + 9} style={{ textAlign: 'center' }}>
                <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>{i + 10}</div>
                <input
                  type="number" min={3} max={6} value={p}
                  onChange={(e) => updatePar(i + 9, e.target.value)}
                  style={{ width: '100%', padding: 6, textAlign: 'center', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 14 }}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="admin-icon-btn">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !valid}
            className="admin-icon-btn"
            data-testid="save-add-scorecard"
            style={{ background: '#2F8F6B', color: '#fff', opacity: saving || !valid ? 0.6 : 1 }}
          >
            <Check size={14} /> {saving ? 'Saving...' : 'Save Scorecard'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Courses Tab =====
const CoursesTab = () => {
  const [view, setView] = useState('pending'); // 'pending' | 'fulfilled' | 'scorecards' | 'par-corrections' | 'user-submitted'
  const [requests, setRequests] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [warmJob, setWarmJob] = useState(null);
  const [warmStarting, setWarmStarting] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('tampa_bay');

  const fetchRequests = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/course-requests?status=${status}`, { headers: getAuthHeaders() });
      setRequests(res.data.requests || []);
    } catch { toast.error('Failed to load requests'); }
    setLoading(false);
  }, []);

  const fetchScorecards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/scorecards`, { headers: getAuthHeaders() });
      setScorecards(res.data.scorecards || []);
    } catch { toast.error('Failed to load scorecards'); }
    setLoading(false);
  }, []);

  const fetchCorrections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/par-corrections?status=pending`, { headers: getAuthHeaders() });
      setCorrections(res.data.corrections || []);
    } catch { toast.error('Failed to load par corrections'); }
    setLoading(false);
  }, []);

  const fetchUserSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/user-submitted-scorecards?status=pending_review`, { headers: getAuthHeaders() });
      setUserSubmissions(res.data.submissions || []);
    } catch { toast.error('Failed to load user submissions'); }
    setLoading(false);
  }, []);

  const fetchWarmStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/scorecards/warm-cache/status`, { headers: getAuthHeaders() });
      setWarmJob(res.data.job || null);
    } catch { /* silent — non-critical */ }
  }, []);

  const fetchRegions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/scorecards/warm-cache/regions`, { headers: getAuthHeaders() });
      setRegions(res.data.regions || []);
    } catch { /* silent */ }
  }, []);

  // Initial loads
  useEffect(() => { fetchWarmStatus(); fetchRegions(); }, [fetchWarmStatus, fetchRegions]);

  // Poll status every 3s while a job is running
  useEffect(() => {
    if (warmJob?.status !== 'running' && warmJob?.status !== 'queued') return;
    const id = setInterval(fetchWarmStatus, 3000);
    return () => clearInterval(id);
  }, [warmJob?.status, fetchWarmStatus]);

  // Auto-refresh scorecards list when a job completes
  const wasRunningRef = React.useRef(false);
  useEffect(() => {
    const isRunning = warmJob?.status === 'running' || warmJob?.status === 'queued';
    if (wasRunningRef.current && !isRunning && warmJob?.status === 'completed') {
      toast.success(`Warm-up complete: ${warmJob.resolved || 0} new courses cached, ${warmJob.failed || 0} failed`);
      if (view === 'scorecards') fetchScorecards();
    }
    wasRunningRef.current = isRunning;
  }, [warmJob, view, fetchScorecards]);

  const startWarmCache = async () => {
    const regionLabel = regions.find(r => r.id === selectedRegion)?.label || selectedRegion;
    const confirmMsg = selectedRegion === 'curated'
      ? `Start warm-up using the curated 350-course list?\n\nRuns in background ~30-60 min. Uses LLM calls (~$3-8 in API costs). You can close this page.`
      : `Discover and warm up ALL golf courses in:\n${regionLabel}\n\nThis queries OpenStreetMap for the region and runs each through the resolver. Runs in background ~30-60 min. You can close this page.`;
    if (!window.confirm(confirmMsg)) return;
    setWarmStarting(true);
    try {
      const body = selectedRegion === 'curated' ? {} : { region: selectedRegion };
      const res = await axios.post(`${API}/admin/scorecards/warm-cache`, body, { headers: getAuthHeaders() });
      toast.success(`Warm-up started (job ${res.data.job_id})`);
      fetchWarmStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start warm-up');
    }
    setWarmStarting(false);
  };

  useEffect(() => {
    if (view === 'scorecards') fetchScorecards();
    else if (view === 'par-corrections') fetchCorrections();
    else if (view === 'user-submitted') fetchUserSubmissions();
    else fetchRequests(view);
  }, [view, fetchRequests, fetchScorecards, fetchCorrections, fetchUserSubmissions]);

  const reject = async (id) => {
    if (!window.confirm('Reject this course request?')) return;
    try {
      await axios.post(`${API}/admin/course-requests/${id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success('Request rejected');
      fetchRequests(view);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };

  const deleteScorecard = async (courseKey) => {
    if (!window.confirm('Delete this scorecard? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/scorecards/${encodeURIComponent(courseKey)}`, { headers: getAuthHeaders() });
      toast.success('Scorecard deleted');
      fetchScorecards();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const refresh = () => {
    if (view === 'scorecards') fetchScorecards();
    else if (view === 'par-corrections') fetchCorrections();
    else if (view === 'user-submitted') fetchUserSubmissions();
    else fetchRequests(view);
  };

  const promoteUserSubmission = async (s, pars) => {
    if (!window.confirm(`Promote "${s.course_name}" to the global scorecard cache?\nPar ${pars.reduce((a,b)=>a+b,0)} | ${s.vote_count} submission${s.vote_count > 1 ? 's' : ''}`)) return;
    try {
      await axios.post(`${API}/admin/user-submitted-scorecards/promote`, {
        course_key: s.course_key,
        course_name: s.course_name,
        lat: s.lat,
        lng: s.lng,
        pars,
      }, { headers: getAuthHeaders() });
      toast.success(`Promoted — '${s.course_name}' now available globally`);
      fetchUserSubmissions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to promote'); }
  };

  const rejectUserSubmission = async (s) => {
    if (!window.confirm(`Reject all submissions for "${s.course_name}"?`)) return;
    try {
      await axios.post(`${API}/admin/user-submitted-scorecards/reject`, {
        course_key: s.course_key,
      }, { headers: getAuthHeaders() });
      toast.success('Rejected');
      fetchUserSubmissions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const acceptCorrection = async (c) => {
    if (!window.confirm(`Accept: change Hole ${c.hole_number} at "${c.course_name}" from par ${c.old_par ?? '?'} to par ${c.new_par}?`)) return;
    try {
      await axios.post(`${API}/admin/par-corrections/accept`, {
        course_name_normalized: c.course_name_normalized,
        hole_number: c.hole_number,
        new_par: c.new_par,
      }, { headers: getAuthHeaders() });
      toast.success(`Hole ${c.hole_number} updated globally`);
      fetchCorrections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const rejectCorrection = async (c) => {
    if (!window.confirm('Reject this par correction?')) return;
    try {
      await axios.post(`${API}/admin/par-corrections/reject`, {
        course_name_normalized: c.course_name_normalized,
        hole_number: c.hole_number,
        new_par: c.new_par,
      }, { headers: getAuthHeaders() });
      toast.success('Rejected');
      fetchCorrections();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const pendingCount = view === 'pending' ? requests.length : null;
  const warmIsActive = warmJob?.status === 'running' || warmJob?.status === 'queued';
  const warmPct = warmJob?.total ? Math.round((warmJob.processed / warmJob.total) * 100) : 0;

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>Courses {pendingCount !== null && `(${pendingCount} pending)`}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            disabled={warmIsActive}
            data-testid="warm-cache-region-select"
            style={{ padding: '6px 10px', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 13, maxWidth: 220 }}
          >
            <option value="curated">📋 Curated List (350 top US courses)</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>📍 {r.label}</option>
            ))}
          </select>
          <button
            onClick={startWarmCache}
            disabled={warmIsActive || warmStarting}
            className="admin-icon-btn"
            data-testid="warm-cache-btn"
            style={{ background: warmIsActive ? '#1a2826' : '#2F8F6B', color: '#fff', opacity: warmIsActive || warmStarting ? 0.6 : 1 }}
          >
            <Zap size={14} /> {warmIsActive ? 'Warming...' : 'Warm Cache'}
          </button>
          <button onClick={() => setShowAdd(true)} className="admin-icon-btn" data-testid="add-scorecard-btn" style={{ background: '#C8A96A', color: '#0F1E1A' }}>
            <Plus size={14} /> Add Scorecard
          </button>
          <button onClick={refresh} className="admin-icon-btn" data-testid="refresh-courses">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {warmJob && (
        <div className="admin-card" style={{ padding: 14, marginBottom: 16 }} data-testid="warm-cache-status">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={14} style={{ color: warmIsActive ? '#C8A96A' : '#2F8F6B' }} />
              <span style={{ color: '#f5f0e8', fontWeight: 600, fontSize: 13 }}>
                Cache Warm-Up: {warmJob.status === 'running' ? 'In Progress' : warmJob.status === 'completed' ? 'Completed' : warmJob.status === 'failed' ? 'Failed' : 'Queued'}
              </span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>{warmJob.id}</span>
            </div>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>{warmJob.started_at?.slice(0, 16).replace('T', ' ')}</span>
          </div>
          {warmJob.total > 0 && (
            <>
              <div style={{ background: '#0F1E1A', borderRadius: 4, overflow: 'hidden', height: 8, marginBottom: 8 }}>
                <div style={{ background: '#2F8F6B', height: '100%', width: `${warmPct}%`, transition: 'width 0.4s' }} />
              </div>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#94a3b8', flexWrap: 'wrap' }}>
                <span>Processed: <strong style={{ color: '#f5f0e8' }}>{warmJob.processed}/{warmJob.total}</strong> ({warmPct}%)</span>
                <span style={{ color: '#2F8F6B' }}>Resolved: <strong>{warmJob.resolved}</strong></span>
                <span style={{ color: '#C8A96A' }}>Already cached: <strong>{warmJob.skipped_cached}</strong></span>
                <span style={{ color: '#ef4444' }}>Failed: <strong>{warmJob.failed}</strong></span>
              </div>
            </>
          )}
          {warmJob.status === 'failed' && warmJob.error && (
            <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>Error: {warmJob.error}</p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #2a3835', paddingBottom: 12, flexWrap: 'wrap' }}>
        {[
          { id: 'pending', label: 'Pending Requests' },
          { id: 'fulfilled', label: 'Fulfilled' },
          { id: 'scorecards', label: 'All Scorecards' },
          { id: 'par-corrections', label: `Par Corrections${view === 'par-corrections' && corrections.length ? ` (${corrections.length})` : ''}` },
          { id: 'user-submitted', label: `User Scorecards${view === 'user-submitted' && userSubmissions.length ? ` (${userSubmissions.length})` : ''}` },
        ].map(opt => (
          <button
            key={opt.id}
            onClick={() => setView(opt.id)}
            data-testid={`courses-view-${opt.id}`}
            className="admin-icon-btn"
            style={{
              background: view === opt.id ? '#0F3B2E' : 'transparent',
              color: view === opt.id ? '#f5f0e8' : '#94a3b8',
              border: view === opt.id ? '1px solid #2F8F6B' : '1px solid #2a3835',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? <p className="admin-loading">Loading...</p> : (
        <>
          {view !== 'scorecards' && requests.length === 0 && (
            <p className="admin-empty">
              {view === 'pending'
                ? 'No pending course requests. When users request a missing scorecard, they will appear here.'
                : 'No fulfilled requests yet.'}
            </p>
          )}

          {view !== 'scorecards' && requests.length > 0 && (
            <div className="admin-activity-list" style={{ gap: 8 }}>
              {requests.map((r) => (
                <div key={r.id} className="admin-card" style={{ padding: 14 }} data-testid={`course-request-${r.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Flag size={14} style={{ color: '#C8A96A', flexShrink: 0 }} />
                        <span style={{ color: '#f5f0e8', fontWeight: 600, fontSize: 14 }}>{r.course_name}</span>
                        {r.status === 'fulfilled' && (
                          <span style={{ background: '#2F8F6B', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4 }}>FULFILLED</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>By {r.user_display || r.user_email || 'unknown'}</span>
                        {r.address && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={11} /> {r.address}</span>}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={11} /> {r.created_at?.slice(0, 10)}</span>
                      </div>
                      {r.course_url && (
                        <a href={r.course_url} target="_blank" rel="noopener noreferrer" style={{ color: '#C8A96A', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                          <ExternalLink size={11} /> {r.course_url}
                        </a>
                      )}
                      {r.notes && (
                        <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>"{r.notes}"</p>
                      )}
                    </div>
                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => setFulfillTarget(r)}
                          className="admin-icon-btn"
                          data-testid={`fulfill-${r.id}`}
                          style={{ background: '#2F8F6B', color: '#fff' }}
                        >
                          <Check size={14} /> Fulfill
                        </button>
                        <button
                          onClick={() => reject(r.id)}
                          className="admin-icon-btn"
                          data-testid={`reject-${r.id}`}
                          style={{ color: '#ef4444' }}
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === 'scorecards' && (
            scorecards.length === 0 ? (
              <p className="admin-empty">No curated scorecards yet. Add one to start the library.</p>
            ) : (
              <div className="admin-activity-list" style={{ gap: 8 }}>
                {scorecards.map((s) => {
                  const total = (s.pars || []).reduce((a, b) => a + b, 0);
                  return (
                    <div key={s.course_key} className="admin-card" style={{ padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#f5f0e8', fontWeight: 600, fontSize: 14 }}>{s.course_name}</span>
                            {s.tee_name && <span style={{ color: '#C8A96A', fontSize: 11 }}>{s.tee_name}</span>}
                            <span style={{ color: '#94a3b8', fontSize: 11 }}>Par {total}</span>
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontFamily: 'monospace' }}>
                            {(s.pars || []).join(' • ')}
                          </div>
                          {s.updated_by && (
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                              Source: {s.updated_by} • {s.updated_at?.slice(0, 10)}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => deleteScorecard(s.course_key)}
                          className="admin-icon-btn"
                          data-testid={`delete-scorecard-${s.course_key}`}
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
          {view === 'par-corrections' && (
            corrections.length === 0 ? (
              <p className="admin-empty">No par corrections pending. When users edit par mid-round, suggestions land here for your verification.</p>
            ) : (
              <div className="admin-activity-list" style={{ gap: 8 }}>
                {corrections.map((c) => (
                  <div key={`${c.course_name_normalized}-${c.hole_number}-${c.new_par}`} className="admin-card" style={{ padding: 14 }} data-testid={`par-correction-${c.hole_number}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ color: '#f5f0e8', fontWeight: 600, fontSize: 14 }}>{c.course_name}</span>
                          <span style={{ color: '#C8A96A', fontSize: 12 }}>Hole {c.hole_number}</span>
                          <span style={{ background: '#0F3B2E', color: '#C8A96A', fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                            {c.old_par ?? '?'} → {c.new_par}
                          </span>
                          {c.vote_count > 1 && (
                            <span style={{ background: '#2F8F6B', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                              {c.vote_count} VOTES
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                          Submitted by {submitterLabel(c.submitters)} • {c.latest_at?.slice(0, 10)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => acceptCorrection(c)}
                          className="admin-icon-btn"
                          data-testid={`accept-correction-${c.hole_number}-${c.new_par}`}
                          style={{ background: '#2F8F6B', color: '#fff' }}
                        >
                          <Check size={14} /> Accept
                        </button>
                        <button
                          onClick={() => rejectCorrection(c)}
                          className="admin-icon-btn"
                          data-testid={`reject-correction-${c.hole_number}-${c.new_par}`}
                          style={{ color: '#ef4444' }}
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          {view === 'user-submitted' && (
            userSubmissions.length === 0 ? (
              <p className="admin-empty">No user-submitted scorecards pending. When users enter pars manually for a missing course, submissions land here.</p>
            ) : (
              <div className="admin-activity-list" style={{ gap: 8 }}>
                {userSubmissions.map((s) => {
                  const pars = s.majority_pars || s.latest_pars || [];
                  const total = pars.reduce((a, b) => a + b, 0);
                  return (
                    <div key={s.course_key} className="admin-card" style={{ padding: 14 }} data-testid={`user-scorecard-${s.course_key}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                            <Flag size={14} style={{ color: '#C8A96A', flexShrink: 0 }} />
                            <span style={{ color: '#f5f0e8', fontWeight: 600, fontSize: 14 }}>{s.course_name}</span>
                            <span style={{ background: '#0F3B2E', color: '#C8A96A', fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                              Par {total}
                            </span>
                            {s.vote_count > 1 && (
                              <span style={{ background: '#2F8F6B', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                                {s.vote_count} SUBMISSIONS
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                            By {submitterLabel(s.submitters)}
                            {s.lat != null && s.lng != null && ` • ${s.lat?.toFixed(2)}, ${s.lng?.toFixed(2)}`}
                          </div>
                          <div style={{ background: '#0F1E1A', padding: 8, borderRadius: 4, fontFamily: 'monospace', fontSize: 11, color: '#f5f0e8' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 2, marginBottom: 4 }}>
                              {pars.slice(0, 9).map((p, i) => (
                                <span key={i} style={{ textAlign: 'center', padding: '2px 0', background: '#1a2826', borderRadius: 2 }}>{p}</span>
                              ))}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 2 }}>
                              {pars.slice(9, 18).map((p, i) => (
                                <span key={i + 9} style={{ textAlign: 'center', padding: '2px 0', background: '#1a2826', borderRadius: 2 }}>{p}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => promoteUserSubmission(s, pars)}
                            className="admin-icon-btn"
                            data-testid={`promote-user-scorecard-${s.course_key}`}
                            style={{ background: '#2F8F6B', color: '#fff' }}
                          >
                            <Check size={14} /> Promote
                          </button>
                          <button
                            onClick={() => rejectUserSubmission(s)}
                            className="admin-icon-btn"
                            data-testid={`reject-user-scorecard-${s.course_key}`}
                            style={{ color: '#ef4444' }}
                          >
                            <X size={14} /> Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </>
      )}

      {fulfillTarget && (
        <FulfillModal
          request={fulfillTarget}
          onClose={() => setFulfillTarget(null)}
          onSaved={() => { setFulfillTarget(null); fetchRequests(view); }}
        />
      )}
      {showAdd && (
        <AddScorecardModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); if (view === 'scorecards') fetchScorecards(); }}
        />
      )}
    </div>
  );
};

const EmailLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [resendInput, setResendInput] = useState('');
  const [resending, setResending] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-log?status=${statusFilter}&kind=${kindFilter}&limit=200`, { headers: getAuthHeaders() });
      setLogs(res.data.logs || []);
    } catch { toast.error('Failed to load email log'); }
    setLoading(false);
  }, [statusFilter, kindFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const resendWelcome = async () => {
    const v = resendInput.trim();
    if (!v) { toast.error('Enter a username or email'); return; }
    setResending(true);
    try {
      const isEmail = v.includes('@');
      const body = isEmail ? { email: v } : { username: v };
      const res = await axios.post(`${API}/admin/resend-welcome`, body, { headers: getAuthHeaders() });
      if (res.data.success) {
        toast.success(`Welcome email sent to ${res.data.user.email}`);
      } else {
        toast.error(`Send failed: ${res.data.result?.reason || res.data.result?.error || 'unknown'}`);
      }
      setResendInput('');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    }
    setResending(false);
  };

  const statusColor = (s) => s === 'sent' ? '#2F8F6B' : s === 'failed' ? '#ef4444' : '#C8A96A';

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>Email Log</h2>
        <button onClick={fetchLogs} className="admin-icon-btn" data-testid="refresh-email-log">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div className="admin-card" style={{ padding: 14, marginBottom: 16 }}>
        <h3 className="admin-card-title" style={{ margin: '0 0 10px', fontSize: 14 }}>Manually Resend Welcome Email</h3>
        <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 10px' }}>
          Enter a username or email. The user must already exist in the database.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={resendInput}
            onChange={(e) => setResendInput(e.target.value)}
            placeholder="username or email"
            data-testid="resend-welcome-input"
            style={{ flex: 1, padding: 8, background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4 }}
            onKeyDown={(e) => e.key === 'Enter' && !resending && resendWelcome()}
          />
          <button
            onClick={resendWelcome}
            disabled={resending}
            className="admin-icon-btn"
            data-testid="resend-welcome-btn"
            style={{ background: '#2F8F6B', color: '#fff', opacity: resending ? 0.6 : 1 }}
          >
            <Send size={14} /> {resending ? 'Sending...' : 'Send Welcome'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 13 }}
          data-testid="filter-email-status"
        >
          <option value="all">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          style={{ padding: '6px 10px', background: '#0F1E1A', color: '#f5f0e8', border: '1px solid #2a3835', borderRadius: 4, fontSize: 13 }}
          data-testid="filter-email-kind"
        >
          <option value="all">All Types</option>
          <option value="welcome">Welcome</option>
        </select>
      </div>

      {loading ? <p className="admin-loading">Loading...</p> : logs.length === 0 ? (
        <p className="admin-empty">No email log entries yet. Once users sign up, their welcome email attempts will appear here.</p>
      ) : (
        <div className="admin-activity-list" style={{ gap: 6 }}>
          {logs.map(l => (
            <div key={l.id} className="admin-card" style={{ padding: 10, fontSize: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    background: statusColor(l.status), color: '#fff',
                    fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                    textTransform: 'uppercase', minWidth: 50, textAlign: 'center',
                  }}>{l.status}</span>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>{l.kind}</span>
                  <span style={{ color: '#f5f0e8', fontFamily: 'monospace', fontSize: 12 }}>{l.recipient}</span>
                  {l.http_status && (
                    <span style={{ color: l.http_status >= 300 ? '#ef4444' : '#94a3b8', fontSize: 11 }}>HTTP {l.http_status}</span>
                  )}
                </div>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{l.created_at?.slice(0, 19).replace('T', ' ')}</span>
              </div>
              {l.error && (
                <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4, paddingLeft: 60, fontFamily: 'monospace' }}>{l.error.slice(0, 250)}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Main Dashboard =====
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [featureUsage, setFeatureUsage] = useState([]);
  const [recentSignups, setRecentSignups] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const [s, f, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/feature-usage`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/recent-signups?days=7`, { headers: getAuthHeaders() }),
      ]);
      setStats(s.data); setFeatureUsage(f.data); setRecentSignups(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  }, [navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'courses', label: 'Courses', icon: Flag },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'email-log', label: 'Email Log', icon: Send },
    { id: 'error-log', label: 'Error Log', icon: AlertTriangle },
    { id: 'push', label: 'Push', icon: Bell },
  ];

  return (
    <div className="admin-layout" data-testid="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <span className="admin-brand-icon">TC</span>
          <span className="admin-brand-text">Admin</span>
        </div>
        <nav className="admin-nav">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`admin-nav-item ${tab === t.id ? 'active' : ''}`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={18} />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={logout} className="admin-nav-item admin-logout" data-testid="admin-logout">
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </aside>
      <main className="admin-main">
        {tab === 'overview' && <OverviewTabWrapper stats={stats} featureUsage={featureUsage} refreshStats={fetchStats} setTab={setTab} recentSignups={recentSignups} />}
        {tab === 'users' && <UsersTab />}
        {tab === 'courses' && <CoursesTab />}
        {tab === 'feedback' && <FeedbackTab />}
        {tab === 'activity' && <ActivityTab />}
        {tab === 'email' && <EmailTab />}
        {tab === 'email-log' && <EmailLogTab />}
        {tab === 'error-log' && <ErrorLogTab />}
        {tab === 'push' && <PushTab />}
      </main>
    </div>
  );
};

// ===== Error Log Tab =====
const ErrorLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0 });
  const [filter, setFilter] = useState('false'); // 'false' = open only, 'true' = resolved, '' = all
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?resolved=${filter}` : '';
      const r = await axios.get(`${API}/admin/error-logs${q}`, { headers: getAuthHeaders() });
      setLogs(r.data.errors || []);
      setStats({ total: r.data.total || 0, open: r.data.open || 0 });
    } catch { toast.error('Failed to load errors'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await axios.post(`${API}/admin/error-logs/${id}/resolve`, {}, { headers: getAuthHeaders() });
      toast.success('Marked resolved');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="admin-tab-content" data-testid="error-log-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>Error Log</h2>
          <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: 13 }}>
            {stats.open} open · {stats.total} total lifetime · Auto-collected from real users
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { v: 'false', label: 'Open' },
            { v: 'true', label: 'Resolved' },
            { v: '', label: 'All' },
          ].map(o => (
            <button
              key={o.label}
              onClick={() => setFilter(o.v)}
              className="admin-icon-btn"
              data-testid={`error-filter-${o.label.toLowerCase()}`}
              style={{
                background: filter === o.v ? '#0F3B2E' : 'transparent',
                color: filter === o.v ? '#f5f0e8' : '#94a3b8',
                border: `1px solid ${filter === o.v ? '#2F8F6B' : '#2a3835'}`,
              }}
            >
              {o.label}
            </button>
          ))}
          <button onClick={load} className="admin-icon-btn"><RefreshCw size={14} /></button>
        </div>
      </div>

      {loading ? (
        <p className="admin-empty">Loading…</p>
      ) : logs.length === 0 ? (
        <p className="admin-empty">No errors — celebrate 🎉</p>
      ) : (
        <div className="admin-activity-list" style={{ gap: 8 }}>
          {logs.map(e => (
            <div key={e.id} className="admin-card" style={{ padding: 14 }} data-testid={`error-log-${e.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <AlertTriangle size={14} style={{ color: e.type === 'boundary' ? '#ef4444' : '#f59e0b' }} />
                    <span style={{ color: '#f5f0e8', fontWeight: 600, fontSize: 14 }}>{e.message || '(no message)'}</span>
                    <span style={{ background: '#0F3B2E', color: '#C8A96A', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>{e.type}</span>
                    {e.environment !== 'production' && (
                      <span style={{ background: '#3a2a15', color: '#f59e0b', fontSize: 10, padding: '2px 6px', borderRadius: 4 }}>{e.environment}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                    {new Date(e.created_at).toLocaleString()} · {e.route || '/'} · v{e.app_version}
                    {e.user_id_hash && ` · ${e.user_id_hash}`}
                  </div>
                  {e.stack && (
                    <pre style={{ background: '#0F1E1A', padding: 8, borderRadius: 4, fontSize: 10, color: '#94a3b8', overflow: 'auto', maxHeight: 120, margin: 0 }}>
                      {e.stack}
                    </pre>
                  )}
                </div>
                {!e.resolved && (
                  <button
                    onClick={() => resolve(e.id)}
                    className="admin-icon-btn"
                    data-testid={`resolve-error-${e.id}`}
                    style={{ background: '#2F8F6B', color: '#fff', height: 32 }}
                  >
                    <Check size={14} /> Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default AdminDashboard;
